/**
 * Relative date tokens supported by embed URLs and web-component attributes so
 * a static iframe `src` or a copy-pasted `<fm-rv-search>` snippet keeps showing
 * up-to-date dates for every visitor.
 *
 * Supported tokens (case-insensitive):
 *   - `today`            -> the visitor's current local date
 *   - `tomorrow`         -> the next day
 *   - `today+N`          -> N days from today (e.g. `today+7`)
 *   - `tomorrow+N`       -> N + 1 days from today
 *
 * Any other value (e.g. an explicit `2026-06-30`) is returned unchanged.
 *
 * Resolution is intentionally based on the viewer's LOCAL time, so it runs in
 * the visitor's browser/timezone — never the server's.
 */

/** Formats a Date to a local `YYYY-MM-DD` string (no UTC shift). */
function toLocalISODate(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

const RELATIVE_DATE_PATTERN = /^(today|tomorrow)(?:\+(\d+))?$/;

/**
 * Resolves a relative date token to a concrete `YYYY-MM-DD` date.
 * Explicit dates and unknown values pass through untouched.
 */
export function resolveEmbedDate(value: string | undefined | null): string {
  if (!value) return value ?? "";

  const match = value.trim().toLowerCase().match(RELATIVE_DATE_PATTERN);
  if (!match) return value;

  const base = new Date();
  base.setHours(0, 0, 0, 0);

  const baseOffset = match[1] === "tomorrow" ? 1 : 0;
  const extraOffset = match[2] ? parseInt(match[2], 10) : 0;
  base.setDate(base.getDate() + baseOffset + extraOffset);

  return toLocalISODate(base);
}
